import { CreateLogger, warn } from "@swivl-mono/helpers";
import {
  EscalationIsAssignedToMember,
  Log,
  RoomStateRequestData,
  SocketEventAvailableMembersUpdated,
  SocketEventNotification,
  SocketEventType,
  SocketEventUpdateEscalation,
  SocketEventUpdateSession,
  SocketEventUpdateUsersOnline,
  SocketEventUserUpdated,
  SubscribableSocketEventType
} from "@swivl-mono/types";
import { Socket, io } from "socket.io-client";

import { Member } from "../Member/Member.Current";
import MembersModel from "../Member/Members.Model";
import NotificationsModel from "../Notifications/Notifications.Model";
import { UsersModel } from "../Users/Users.Model";
import { UsersOnlineManager } from "../Users/UsersOnline.Manager";
import { AttachLifecycleEvents } from "./Helpers/LifecycleEvents.Helper";

const log = CreateLogger(Log.NOTIFICATIONS, "swivl");

export default class SocketEventsManager {
  private static socket: Socket;

  public static visibility: DocumentVisibilityState = document.visibilityState;
  public static wentInvisibleAt?: number;
  public static lifecycleListenersAttached = false;

  static _state: "disconnected" | "connecting" | "connected" | "reconnecting" = "disconnected";
  public static get state() {
    return this._state;
  }
  public static set state(value) {
    if (this._state !== value) {
      log("info", "socket", "state_change", { from: this._state, to: value });
      this._state = value;
    }
  }

  // Section 1: Connection setup and teardown
  public static async connect(memberId: number): Promise<void> {
    log("Initializing socketio SocketEventsManager via", import.meta.env.VITE_NOTIFICATIONS_WEBSOCKET_URL);

    if (SocketEventsManager.socket) {
      if (SocketEventsManager.socket.connected) {
        log("SocketEventsManager connect - connection is already set up");
        return;
      }
      log("SocketEventsManager connect - socket exists but not connected, cleaning up old socket");
      SocketEventsManager.socket.close();
    }
    AttachLifecycleEvents();
    try {
      SocketEventsManager.socket = io(import.meta.env.VITE_NOTIFICATIONS_WEBSOCKET_URL, {
        path: "/notifications/sio",
        transports: ["websocket"], // AdamTodo: ensure this is correct
        query: {
          memberId: memberId // Server will use memberId to subscribe to notifications
        },
        autoConnect: true,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000 // 20 seconds
      });
    } catch (error) {
      warn("SocketEventsManager error: Unable to connect.", error);
      return;
    }

    SocketEventsManager.socket.on("message", (event) => {
      log("SocketEventsManager generic 'message':", event);
      // We're not using this, we're using custom emits (see below)
    });

    // const socketOnError = (event: any) => {
    //   warn("SocketEventsManager socketOnError", event);
    // };
    const socketOnClose = (event: any) => {
      warn("SocketEventsManager socketOnClose", event);
    };

    // SocketEventsManager.socket.on("error", socketOnError);
    // SocketEventsManager.socket.on("connect_error", socketOnError);
    // SocketEventsManager.socket.on("close", socketOnClose);
    // SocketEventsManager.socket.on("disconnect", socketOnClose);

    SocketEventsManager.socket.on("connect", () => {
      this.state = "connected";
      console.log("✅ WebSocket CONNECTED:", SocketEventsManager.socket.id);
      log("info", "socket", "connect", { id: SocketEventsManager.socket.id });
    });

    SocketEventsManager.socket.on("disconnect", (reason) => {
      this.state = "disconnected";
      warn("warn", "socket", "disconnect", { reason });
    });

    SocketEventsManager.socket.on("error", (err: unknown) => {
      // often fires on backgrounded tabs / network flaps
      if (this.state !== "connected") this.state = "reconnecting";
      warn("warn", "socket", "connect_error", err);
    });

    SocketEventsManager.socket.on("connect_error", (err: unknown) => {
      // often fires on backgrounded tabs / network flaps
      if (this.state !== "connected") this.state = "reconnecting";
      warn("warn", "socket", "connect_error", err);
    });

    // Manager-level reconnection lifecycle (not always documented the same across versions, but widely supported)
    // @ts-ignore
    SocketEventsManager.socket.io?.on("reconnect_attempt", (attempt: number) => {
      this.state = "reconnecting";
      log("info", "socket", "reconnect_attempt", { attempt });
    });
    // @ts-ignore
    SocketEventsManager.socket.io?.on("reconnect", (attempt: number) => {
      this.state = "connected";
      log("info", "socket", "reconnect", { attempt });
      this.runResync();
    });
    // @ts-ignore
    SocketEventsManager.socket.io?.on("reconnect_error", (err: unknown) => {
      this.state = "reconnecting";
      log("warn", "socket", "reconnect_error", err);
    });
    // @ts-ignore
    SocketEventsManager.socket.io?.on("reconnect_failed", () => {
      this.state = "disconnected";
      log("error", "socket", "reconnect_failed");
    });

    // Optional: measure round-trip latency via built-in ping/pong (varies by version)
    // @ts-ignore
    SocketEventsManager.socket.io?.on?.("ping", () => log("debug", "pulse", "ping"));
    // @ts-ignore
    SocketEventsManager.socket.io?.on?.("pong", (ms: number) => log("debug", "pulse", "pong", { ms }));

    // Handle receiving different frame types
    SocketEventsManager.socket.on(SocketEventType.AVAILABLE_MEMBERS, SocketEventsManager.receiveMemberAvailability);
    SocketEventsManager.socket.on(SocketEventType.SESSION, SocketEventsManager.receiveSessions);
    SocketEventsManager.socket.on(SocketEventType.ESCALATION, SocketEventsManager.receiveEscalations);
    SocketEventsManager.socket.on(SocketEventType.VARS, SocketEventsManager.receiveVars);
    SocketEventsManager.socket.on(SocketEventType.NOTIFICATION, SocketEventsManager.receiveNotification);
    SocketEventsManager.socket.on(SocketEventType.USERS_ONLINE, SocketEventsManager.receivedUsersOnline);
    SocketEventsManager.socket.on(SocketEventType.USER_UPDATED, SocketEventsManager.receivedUsersUpdated);

    SocketEventsManager.socket.on("connect", () => {
      if (SocketEventsManager.socket.recovered) {
        log("SocketEventsManager connected", SocketEventsManager.socket.id);
        log("SocketEventsManager connection recovered");
      }
      log("SocketEventsManager connect");
      // Set initial global subscriptions (NOTIFICATION is always included, AVAILABLE_MEMBERS for nav bar). Ticket events ride the bot socket now (ENG-3005).
      SocketEventsManager.setSubscriptionState([SocketEventType.NOTIFICATION, SocketEventType.AVAILABLE_MEMBERS]);
    });
  }

  // Section 2. UI calls this to set the desired state of subscriptions.
  // Typed with a generic (sorry) to ensure userId is provided for VARS
  /**
   * @description Sets the complete subscription state for all socket events.  Subscriptions will be modified to match the provided state.  No need to set NOTIFICATIONS, it's always subscribed.
   * @param subscribedEvents - An array of SocketEventType.(AVAILABLE_MEMBERS | SESSIONS | ESCALATIONS | VARS).
   * @param userId - Optional userId if subscribing to VARS.
   * @example
   *  SocketEventsManager.setSubscriptionState([
   *    SocketEventType.SESSIONS,
   *    SocketEventType.ESCALATIONS,
   *  ]);
   */

  static subscriptionEventToSubscribeTo?: SubscribableSocketEventType[];
  static subscriptionUserId?: number;

  static safePing() {
    try {
      if (this.socket?.connected) {
        // Any emit is fine as a keepalive; ideally you have a "ping" server event.
        this.socket.emit("client:ping");
      } else {
        this.tryReconnect();
      }
    } catch (err) {
      log("warn", "pulse", "safePing_error", err);
    }
  }

  static tryReconnect() {
    if (!this.socket) return;
    if (!this.socket.connected) {
      log("info", "socket", "reconnect_call");
      // manager will handle deduping attempts
      // @ts-ignore
      this.socket.io?.reconnect?.();
      // fallback: force open()
      // @ts-ignore
      this.socket.io?.open?.();
    }
  }

  static async runResync() {
    log("info", "resync", "complete");
    UsersModel.loadAll();
  }

  public static setSubscriptionState(subscriptionEventToSubscribeTo: SubscribableSocketEventType[], subscriptionUserId?: number) {
    SocketEventsManager.subscriptionEventToSubscribeTo = subscriptionEventToSubscribeTo;
    SocketEventsManager.subscriptionUserId = subscriptionUserId;
    SocketEventsManager.subscribeToEvents();
  }

  private static subscribeToEvents() {
    log("SocketEventsManager setSubscriptionState:", SocketEventsManager.subscriptionEventToSubscribeTo);
    if (!SocketEventsManager.socket) {
      warn("SocketEventsManager subscribeToEvents - socket is not connected");
      return;
    }
    const RoomStateRequestData: RoomStateRequestData = {
      jwt: Member.jwt,
      orgId: Member.orgId,
      subscriptions: {
        [SocketEventType.NOTIFICATION]: { memberId: Member.id! }, // Always use Member.id for notifications
        [SocketEventType.ESCALATION]: { orgId: Member.orgId },
        [SocketEventType.AVAILABLE_MEMBERS]: { orgId: Member.orgId } // Always subscribe to available members for nav bar
      }
    };
    let subscriptionEventToSubscribeTo: SubscribableSocketEventType[] = [
      SocketEventType.NOTIFICATION, // Always subscribe to notifications
      SocketEventType.AVAILABLE_MEMBERS // Always subscribe to available members
    ]; // This var lets us insure that we always know which events we are subscribed to.
    if (SocketEventsManager.subscriptionEventToSubscribeTo?.includes(SocketEventType.VARS) && SocketEventsManager.subscriptionUserId) {
      RoomStateRequestData.subscriptions[SocketEventType.VARS] = { userId: SocketEventsManager.subscriptionUserId };
      subscriptionEventToSubscribeTo.push(SocketEventType.VARS);
    }
    // AVAILABLE_MEMBERS is now always included above, no need to conditionally add it
    // if (SocketEventsManager.subscriptionEventToSubscribeTo?.includes(SocketEventType.AVAILABLE_MEMBERS)) {
    //   RoomStateRequestData.subscriptions[SocketEventType.AVAILABLE_MEMBERS] = { orgId: Member.orgId };
    //   subscriptionEventToSubscribeTo.push(SocketEventType.AVAILABLE_MEMBERS);
    // }
    if (SocketEventsManager.subscriptionEventToSubscribeTo?.includes(SocketEventType.SESSION)) {
      RoomStateRequestData.subscriptions[SocketEventType.SESSION] = { orgId: Member.orgId };
      subscriptionEventToSubscribeTo.push(SocketEventType.SESSION);
    }
    // if (SocketEventsManager.subscriptionEventToSubscribeTo?.includes(SocketEventType.ESCALATION)) {
    //   RoomStateRequestData.subscriptions[SocketEventType.ESCALATION] = { orgId: Member.orgId };
    //   subscriptionEventToSubscribeTo.push(SocketEventType.ESCALATION);
    // }
    if (SocketEventsManager.subscriptionEventToSubscribeTo?.includes(SocketEventType.USERS_ONLINE)) {
      RoomStateRequestData.subscriptions[SocketEventType.USERS_ONLINE] = { orgId: Member.orgId };
      subscriptionEventToSubscribeTo.push(SocketEventType.USERS_ONLINE);
    }
    // Contact Center V3 subscriptions
    if (SocketEventsManager.subscriptionEventToSubscribeTo?.includes(SocketEventType.CC_CONVERSATION_UPDATE)) {
      RoomStateRequestData.subscriptions[SocketEventType.CC_CONVERSATION_UPDATE] = { orgId: Member.orgId };
      subscriptionEventToSubscribeTo.push(SocketEventType.CC_CONVERSATION_UPDATE);
    }
    if (SocketEventsManager.subscriptionEventToSubscribeTo?.includes(SocketEventType.CC_ASSIGNMENT)) {
      RoomStateRequestData.subscriptions[SocketEventType.CC_ASSIGNMENT] = { memberId: Member.id! };
      subscriptionEventToSubscribeTo.push(SocketEventType.CC_ASSIGNMENT);
    }
    if (SocketEventsManager.subscriptionEventToSubscribeTo?.includes(SocketEventType.CC_PRESENCE)) {
      RoomStateRequestData.subscriptions[SocketEventType.CC_PRESENCE] = { orgId: Member.orgId };
      subscriptionEventToSubscribeTo.push(SocketEventType.CC_PRESENCE);
    }

    if (
      SocketEventsManager.subscriptionEventToSubscribeTo?.length &&
      SocketEventsManager.subscriptionEventToSubscribeTo.length !== subscriptionEventToSubscribeTo.length
    ) {
      warn(
        "SocketEventsManager setSubscriptionState - subscriptionEventToSubscribeTo does not match Room",
        SocketEventsManager.subscriptionEventToSubscribeTo.length,
        subscriptionEventToSubscribeTo.length
      );
      SocketEventsManager.subscriptionEventToSubscribeTo = subscriptionEventToSubscribeTo;
    }
    log("SocketEventsManager to events:", SocketEventsManager.subscriptionEventToSubscribeTo, RoomStateRequestData);
    console.log(`📡 Member ${Member.id} subscribing to rooms:`, {
      events: subscriptionEventToSubscribeTo,
      availableMembersOrgId: RoomStateRequestData.subscriptions[SocketEventType.AVAILABLE_MEMBERS]?.orgId,
      notificationMemberId: RoomStateRequestData.subscriptions[SocketEventType.NOTIFICATION]?.memberId
    });
    // Send state request to server
    SocketEventsManager.socket.emit(SocketEventType.META_ROOM_STATE_REQUEST, RoomStateRequestData);
  }

  // Section 3: Handle incoming subscribed data from the server
  private static receiveMemberAvailability(update: SocketEventAvailableMembersUpdated) {
    console.log(`🔔 Member ${Member.id} RECEIVED MEMBER AVAILABILITY UPDATE for orgId ${update.orgId}:`, update);
    console.log(`   Available members count: ${Object.keys(update.payload).length}`);
    log("SocketEventsManager receiveAgent:", update);
    MembersModel.memberAvailabilityUpdated(update.payload);
    // todo AgentModel.agentOnlineUpdated(agentUpdate.payload);
  }
  private static receiveSessions(sessionUpdate: SocketEventUpdateSession) {
    console.log("PUF SocketEventsManager receiveSessions:", sessionUpdate);
    UsersModel.sessionUpdated(sessionUpdate.payload);
  }
  private static receiveEscalations(escalationsUpdate: SocketEventUpdateEscalation) {
    log("SocketEventsManager receiveEscalations:", escalationsUpdate);
    UsersModel.escalationUpdated(escalationsUpdate.payload);
    // todo ConversationsModel.updateEscalation(escalationsUpdate.payload);
  }
  private static receiveVars(varsUpdate: any) {
    log("SocketEventsManager receiveVars:", varsUpdate);
    // todo Handle vars update
  }
  private static receiveNotification(notificationsUpdate: SocketEventNotification) {
    log("SocketEventsManager receiveNotifications:", notificationsUpdate);
    NotificationsModel.updateNotifications([notificationsUpdate.payload]);
    // todo Handle notifications update
  }
  private static receivedUsersOnline(usersOnlineUpdate: SocketEventUpdateUsersOnline) {
    log("SocketEventsManager receivedUsersOnline:", usersOnlineUpdate);
    UsersOnlineManager.updateUsersOnline(usersOnlineUpdate.payload);
  }
  private static receivedUsersUpdated(event: SocketEventUserUpdated) {
    log("SocketEventsManager userUpdatedEvent:", event);
    UsersModel.updateSingleUser(event.userId, event.payload);
  }
}
